import { useCallback, useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { Alert, Button, Center, Checkbox, Divider, Group, Loader, Paper, Stack, Text, TextInput, ThemeIcon, Title } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck, IconFileText, IconShieldCheck } from '@tabler/icons-react';
import {
  checkPortalSession,
  completePortalSubmission,
  exchangeInvitation,
  PortalUnavailableError,
  PortalUploadError,
  uploadPortalFile,
  type PortalConfirmation,
  type PortalSession,
} from '../api/patientPortal';
import AttachmentUploadField from '../components/AttachmentUploadField';
import FormAlert, { type FormAlertState } from '../components/FormAlert';
import PageShell from '../components/PageShell';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import type { FieldSchema } from '../types/schema';
import NotFoundPage from './NotFoundPage';

type AccessState = 'checking' | 'granted' | 'denied' | 'unavailable';

const INVALID_LINK_MESSAGE = 'This link is invalid or has expired. Please contact your RIS Rx representative to request a new link.';
const UNAVAILABLE_MESSAGE = 'Something went wrong. Please try again.';
const FORM_ALERT_TITLE = 'Unable to continue';
const UPLOAD_NOT_ACCEPTED_MESSAGE = "We couldn't upload your documents. Please contact your RIS Rx representative.";
const UPLOAD_INVALID_MESSAGE = "One of your files can't be uploaded. Please remove it and try again.";

const FILE_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
};

// "Up to 3 files · PDF, JPG, or PNG · 5 MB max each", built from the documents field's limits.
function describeDocumentLimits(field: FieldSchema): string {
  const maxFiles = field.maxFiles ?? 3;
  const labels = (field.acceptedFileTypes ?? []).map((type) => FILE_TYPE_LABELS[type] ?? type);
  const types = labels.length <= 2 ? labels.join(' or ') : `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
  const megabytes = (field.maxSizeBytes ?? 5 * 1024 * 1024) / (1024 * 1024);
  const size = `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)} MB max each`;
  return [`Up to ${maxFiles} file${maxFiles === 1 ? '' : 's'}`, types, size].filter(Boolean).join(' · ');
}

function newUploadKey(): string {
  return crypto.randomUUID();
}

// Asks the session itself whether it is dead. A failing status call counts as alive, because
// the session state is unknown and the page keeps the form so the patient can retry.
async function sessionIsDead(): Promise<boolean> {
  try {
    return (await checkPortalSession()).access === 'denied';
  } catch {
    return false;
  }
}

// The worst failure decides the message: a dead session beats a refusal, which beats a bad file,
// which beats an outage the patient can simply retry.
function uploadFailureKind(kinds: Set<string>): 'denied' | 'notAccepted' | 'invalid' | 'unavailable' | null {
  for (const kind of ['denied', 'notAccepted', 'invalid', 'unavailable'] as const) {
    if (kinds.has(kind)) return kind;
  }
  return null;
}

// The invitation secret arrives in the URL fragment as #k=<token>.
function readInvitationKey(hash: string): string {
  return new URLSearchParams(hash.replace(/^#/, '')).get('k')?.trim() ?? '';
}

// React StrictMode runs effects twice in development. Sharing one in-flight exchange per key
// means a single POST is sent (the same approach as AuthVerifyPage). The entry is dropped once
// the request settles so a retry sends a fresh request.
const exchangeRequests = new Map<string, Promise<PortalSession>>();

function exchangeInvitationOnce(key: string): Promise<PortalSession> {
  const existing = exchangeRequests.get(key);
  if (existing) return existing;
  const request = exchangeInvitation(key).finally(() => exchangeRequests.delete(key));
  exchangeRequests.set(key, request);
  return request;
}

// Login-free page at /p where a patient uploads documents to an open escalation (ESCIQ-198).
// The invitation secret arrives in the URL fragment (#k=...). On load it is moved into a ref and
// removed from the address bar, then traded for a short-lived session cookie (ESCIQ-195). The
// secret is never written to storage and is dropped from memory once the exchange is granted or
// denied. The session answers with the Copay Card ID of the escalation, which the page shows
// read-only (ESCIQ-240); the patient never types it. On submit the files are uploaded to BenIQ
// through the portal API, and a confirmation with a reference is shown. Card ID and files stay in
// memory only.
export default function PatientUploadPage() {
  const { data: config, isLoading } = useResolvedProgram();
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<(PortalConfirmation & { receiptRequested: boolean }) | null>(null);
  const [receiptRequested, setReceiptRequested] = useState(false);
  const [doneFiles, setDoneFiles] = useState<ReadonlySet<File>>(new Set());
  const [access, setAccess] = useState<AccessState>('checking');
  const [cardId, setCardId] = useState<string | undefined>();
  const [formAlert, setFormAlert] = useState<FormAlertState | undefined>();
  const invitationKey = useRef('');
  // One key per submission, kept across retries so the server sees a retry as the same submission.
  const submissionKey = useRef(newUploadKey());
  const fileKeys = useRef(new WeakMap<File, string>());
  const abortController = useRef<AbortController | null>(null);

  const openSession = useCallback(async () => {
    setAccess('checking');
    const key = invitationKey.current;
    try {
      const result = key ? await exchangeInvitationOnce(key) : await checkPortalSession();
      invitationKey.current = '';
      setCardId(result.cardId);
      setAccess(result.access);
    } catch (err) {
      // Keep the key for a retry only when the failure is retryable.
      if (err instanceof PortalUnavailableError) {
        setAccess('unavailable');
      } else {
        invitationKey.current = '';
        setAccess('denied');
      }
    }
  }, []);

  useEffect(() => {
    // Capture first, even while the config is loading, so the secret never lingers in the URL.
    const key = readInvitationKey(window.location.hash);
    if (key) {
      invitationKey.current = key;
    }
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    void openSession();
  }, [openSession]);

  // Cancel requests in progress when the page goes away.
  useEffect(() => () => abortController.current?.abort(), []);

  if (isLoading || access === 'checking') {
    return <Center h="100vh"><Loader size="lg" /></Center>;
  }

  const page = config?.solution === 'patient-upload-portal' ? config.patientUpload : undefined;
  if (!config || !page) {
    return <NotFoundPage />;
  }

  const canSubmit = files.length > 0 && !isSubmitting;

  const handleFilesChange = (next: File[]) => {
    setFiles(next);
    setDoneFiles((current) => new Set(next.filter((file) => current.has(file))));
  };

  const fileKey = (file: File) => {
    let key = fileKeys.current.get(file);
    if (!key) {
      key = newUploadKey();
      fileKeys.current.set(file, key);
    }
    return key;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (abortController.current || isSubmitting || files.length === 0) return;
    setIsSubmitting(true);
    const controller = new AbortController();
    abortController.current = controller;
    try {
      // Files that already finished are not sent again; a retry only repeats the failed ones.
      const pending = files.filter((file) => !doneFiles.has(file));
      const outcomes = await Promise.allSettled(
        pending.map((file) => uploadPortalFile(submissionKey.current, file, fileKey(file), controller.signal)),
      );
      if (controller.signal.aborted) return;

      const done = new Set(doneFiles);
      const kinds = new Set<string>();
      outcomes.forEach((outcome, index) => {
        if (outcome.status === 'fulfilled') {
          done.add(pending[index]);
        } else {
          const kind = outcome.reason instanceof PortalUploadError ? outcome.reason.kind : 'unavailable';
          kinds.add(kind);
          // A refused or invalid file will not succeed on the same reservation, and the server
          // drops its row, so the next attempt must be a fresh reservation. An unavailable
          // failure keeps the key, so a retry resumes the reservation that is still there.
          if (kind === 'notAccepted' || kind === 'invalid') fileKeys.current.delete(pending[index]);
        }
      });
      setDoneFiles(done);

      let failure = uploadFailureKind(kinds);
      let confirmed: PortalConfirmation | null = null;
      if (!failure) {
        try {
          confirmed = await completePortalSubmission(submissionKey.current, receiptRequested, controller.signal);
        } catch (err) {
          if (controller.signal.aborted) return;
          failure = err instanceof PortalUploadError ? err.kind : 'unavailable';
        }
      }
      if (failure === 'denied') {
        // A 404 or a WAF 403 is not always session death (one WAF block, or an attachment the
        // server no longer knows), so keep the form and files unless the session itself is dead.
        const dead = await sessionIsDead();
        if (controller.signal.aborted) return;
        if (dead) {
          setAccess('denied');
        } else {
          setFormAlert({ title: FORM_ALERT_TITLE, message: UNAVAILABLE_MESSAGE });
        }
        return;
      }
      if (failure) {
        const message = failure === 'notAccepted' ? UPLOAD_NOT_ACCEPTED_MESSAGE
          : failure === 'invalid' ? UPLOAD_INVALID_MESSAGE
          : UNAVAILABLE_MESSAGE;
        setFormAlert({ title: FORM_ALERT_TITLE, message });
        return;
      }
      setFormAlert(undefined);
      setConfirmation({ ...confirmed!, receiptRequested });
    } finally {
      if (abortController.current === controller) abortController.current = null;
      setIsSubmitting(false);
    }
  };

  const handleSubmitAnother = () => {
    setFiles([]);
    setDoneFiles(new Set());
    setReceiptRequested(false);
    setFormAlert(undefined);
    submissionKey.current = newUploadKey();
    setConfirmation(null);
  };

  let content;
  if (access === 'denied') {
    content = (
      <Alert color="red" variant="light" icon={<IconAlertCircle />} title={INVALID_LINK_MESSAGE} />
    );
  } else if (access === 'unavailable') {
    content = (
      <Stack gap="sm" align="flex-start">
        <Alert color="red" variant="light" icon={<IconAlertCircle />} title={UNAVAILABLE_MESSAGE} w="100%" />
        <Button className="portal-primary-button" onClick={() => void openSession()}>Try again</Button>
      </Stack>
    );
  } else if (confirmation) {
    // No line under the title when the patient did not ask for a receipt, unless the config gives one.
    const confirmationMessage = confirmation.receiptRequested
      ? page.confirmation.messageWithReceipt
      : page.confirmation.messageWithoutReceipt;
    // Colours follow the portal's primary colour, as in the product mockup: a soft badge behind
    // the check, a tinted card border, and a grey box for the reference and file names. The border
    // is set through --portal-border because .portal-panel fixes its border colour with !important.
    content = (
      <Paper p="lg" withBorder radius="md" className="portal-panel" style={{ '--portal-border': 'rgb(var(--primary-color-rgb) / 0.3)' } as CSSProperties}>
        <Stack gap="md" align="flex-start">
          <ThemeIcon size={48} radius="xl" style={{ backgroundColor: 'rgb(var(--primary-color-rgb) / 0.12)', color: 'var(--primary-color)' }} aria-hidden>
            <IconCircleCheck size={22} />
          </ThemeIcon>
          <div>
            <Title order={3} fw={600}>{page.confirmation.title}</Title>
            {confirmationMessage && <Text size="sm" c="dimmed" mt={6}>{confirmationMessage}</Text>}
          </div>
          <Paper p="md" withBorder radius="md" w="100%" bg="gray.0">
            <Text size="sm" c="dimmed">{page.confirmation.referenceLabel}</Text>
            <Text fw={600} size="lg">{confirmation.reference}</Text>
            <Divider my="sm" />
            <Stack gap={6}>
              {confirmation.files.map((name, index) => (
                <Group key={`${index}-${name}`} gap="xs" wrap="nowrap">
                  <IconFileText size={16} color="var(--primary-color)" style={{ flexShrink: 0 }} />
                  <Text size="sm" style={{ wordBreak: 'break-word' }}>{name}</Text>
                </Group>
              ))}
            </Stack>
          </Paper>
          <Button className="portal-primary-button" onClick={handleSubmitAnother}>
            {page.confirmation.submitAnotherLabel}
          </Button>
        </Stack>
      </Paper>
    );
  } else {
    content = (
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Stack gap="lg">
          <FormAlert alert={formAlert} />
          {/* Left out when the API could not load a Card ID; the patient can still upload. */}
          {cardId && (
            <Paper p="lg" withBorder radius="md" className="portal-panel">
              <Title order={4} mb="md" className="portal-section-title">Your information</Title>
              <div data-field-id={page.cardIdField.id}>
                <TextInput label={page.cardIdField.label} value={cardId} readOnly size="sm" radius="md" />
              </div>
            </Paper>
          )}
          <Paper p="lg" withBorder radius="md" className="portal-panel">
            <Title order={4} className="portal-section-title">{page.documentsField.label ?? 'Documents'}</Title>
            <Text size="sm" c="dimmed" mb="md">{describeDocumentLimits(page.documentsField)}</Text>
            <AttachmentUploadField
              field={page.documentsField}
              value={files}
              onChange={handleFilesChange}
              disabled={isSubmitting}
              removeDisabled={isSubmitting}
              browseLabel={page.browseLabel ?? 'Browse files'}
              hideLabel
              doneFiles={doneFiles}
            />
          </Paper>
          <Paper p="md" withBorder radius="md" className="portal-panel">
            <Checkbox
              label={page.receiptOptInLabel}
              checked={receiptRequested}
              onChange={(event) => setReceiptRequested(event.currentTarget.checked)}
              // Once a file is stored the server fixes the choice at the first completion, so
              // the confirmation text must not be able to disagree with it.
              disabled={isSubmitting || doneFiles.size > 0}
            />
          </Paper>
          <Group justify="space-between" align="center" wrap="wrap" gap="md">
            {page.disclaimer ? (
              <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 240 }}>
                <IconShieldCheck size={18} color="var(--mantine-color-green-7)" style={{ flexShrink: 0 }} />
                <Text size="xs" c="dimmed">{page.disclaimer}</Text>
              </Group>
            ) : <span />}
            <Button type="submit" className="portal-primary-button" disabled={!canSubmit} loading={isSubmitting}>
              {page.submitLabel}
            </Button>
          </Group>
        </Stack>
      </form>
    );
  }

  return (
    <PageShell
      config={config}
      headerTitle={page.title}
      hideTitle={Boolean(confirmation)}
      headerSubtitle={page.subtitle}
      showFooter
      support={config.support}
    >
      {content}
    </PageShell>
  );
}
